import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { supabase, priceHelpers } from '../utils/supabase';
import {
  StarIcon,
  EyeIcon,
  ShoppingCartIcon,
  FunnelIcon,
  Squares2X2Icon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';

const ProductsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart: addToCartContext } = useCart();
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // URL parametrelerini oku
  useEffect(() => {
    const searchParam = searchParams.get('search') || '';
    const categoryParam = searchParams.get('category') || 'all';
    
    setSearchQuery(searchParam);
    setSelectedCategory(categoryParam);
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Tüm ürünleri getir
        const { data: productsData } = await supabase
          .from('products')
          .select('*')
          .eq('is_active', true);

        // Kategorileri getir
        const { data: categoriesData } = await supabase
          .from('categories')
          .select('*')
          .order('name');

        if (productsData) {
          // Fiyatları format et (kar oranı dahil)
          const formattedProducts = await priceHelpers.formatProductList(
            productsData.map(product => {
              // Görsel URL'sini düzelt - images array'inden al
              let imageUrl = product.image_url;
              if (Array.isArray(product.images) && product.images.length > 0) {
                const mainImage = product.images.find(img => img.is_main);
                imageUrl = mainImage?.url || product.images[0]?.url || product.image_url;
              }
              
              console.log(`Product ${product.name} - Original image_url:`, product.image_url, 'Images array:', product.images, 'Final imageUrl:', imageUrl);
              
              return {
                ...product,
                rating: 4.5 + Math.random() * 0.5, // Geçici rating
                reviews: Math.floor(Math.random() * 200) + 50, // Geçici review sayısı
                image_url: imageUrl
              };
            })
          );
          
          // Takım ürünleri için doğru fiyat hesaplama
          const productsWithCorrectPrices = await Promise.all(
            formattedProducts.map(async (product) => {
              if (product.is_set && product.set_items && Array.isArray(product.set_items)) {
                // Takım ürünü için kar oranı dahil toplam fiyat hesapla
                let setItemsTotal = 0;
                for (const item of product.set_items) {
                  const salePrice = await priceHelpers.calculateSalePrice(item.price);
                  setItemsTotal += item.quantity * salePrice;
                }
                
                // İndirim hesapla
                const settings = await priceHelpers.getSettings();
                const discount = Math.round(setItemsTotal * (settings.set_discount_rate / 100) * 100) / 100;
                const finalPrice = setItemsTotal - discount;
                
                return {
                  ...product,
                  set_original_price: setItemsTotal,
                  set_discount: discount,
                  set_final_price: finalPrice
                };
              }
              return product;
            })
          );
          
          console.log('Products with correct prices:', productsWithCorrectPrices); // Debug için
          setProducts(productsWithCorrectPrices);
        }

        if (categoriesData) {
          const formattedCategories = [
            { id: 'all', name: 'Tümü' },
            ...categoriesData.map(cat => ({
              id: cat.name,
              name: cat.name
            }))
          ];
          setCategories(formattedCategories);
        }
      } catch (error) {
        console.error('Data fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const addToCart = (product) => {
    if (product.is_set) {
      // Takım ürünü için - sadece temel bilgilerle ekle, detay sayfasında özelleştirsin
      const cartItem = {
        id: product.id,
        name: product.name,
        price: product.set_final_price,
        image: product.image_url,
        isSet: true,
        category: product.category
      };
      addToCartContext(cartItem, 1);
    } else {
      // Normal ürün için
      const cartItem = {
        id: product.id,
        name: product.name,
        price: product.sale_price || product.price, // sale_price varsa onu, yoksa price'ı kullan
        image: product.image_url,
        isSet: false,
        category: product.category
      };
      addToCartContext(cartItem, 1);
    }
  };

  const sortOptions = [
    { id: 'name', name: 'İsme Göre' },
    { id: 'price-low', name: 'Fiyat (Düşük → Yüksek)' },
    { id: 'price-high', name: 'Fiyat (Yüksek → Düşük)' },
    { id: 'rating', name: 'En Yüksek Puan' },
  ];

  // Filter and sort products
  const filteredProducts = products
    .filter(product => {
      // Kategori filtresi
      const categoryMatch = selectedCategory === 'all' || product.category === selectedCategory;
      
      // Arama filtresi
      let searchMatch = true;
      if (searchQuery && searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        searchMatch = product.name.toLowerCase().includes(query) ||
                     product.description?.toLowerCase().includes(query) ||
                     product.category.toLowerCase().includes(query) ||
                     product.tags?.toLowerCase().includes(query);
      }
      
      return categoryMatch && searchMatch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'price-low':
          const priceA = a.is_set ? a.set_final_price : (a.sale_price || a.price);
          const priceB = b.is_set ? b.set_final_price : (b.sale_price || b.price);
          return priceA - priceB;
        case 'price-high':
          const priceHighA = a.is_set ? a.set_final_price : (a.sale_price || a.price);
          const priceHighB = b.is_set ? b.set_final_price : (b.sale_price || b.price);
          return priceHighB - priceHighA;
        case 'rating':
          return b.rating - a.rating;
        default:
          return 0;
      }
    });

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-luxury font-bold text-gray-900 mb-4">
            {searchQuery ? `"${searchQuery}" için Arama Sonuçları` : 'Ürün Kataloğu'}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {searchQuery 
              ? `${filteredProducts.length} ürün bulundu`
              : 'Premium kalite mobilyalarımızı keşfedin. Her bütçeye uygun seçenekler ve ücretsiz teslimat imkanı.'
            }
          </p>
          
          {/* Arama Sonuçları Bilgisi */}
          {searchQuery && (
            <div className="mt-4 max-w-xl mx-auto">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-700 font-medium">
                  "{searchQuery}" araması için {filteredProducts.length} sonuç gösteriliyor
                </p>
                {selectedCategory !== 'all' && (
                  <p className="text-blue-600 text-sm mt-1">
                    Kategori: {categories.find(c => c.id === selectedCategory)?.name}
                  </p>
                )}
                <button
                  onClick={() => {
                    setSearchParams({});
                    setSearchQuery('');
                    setSelectedCategory('all');
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-2 inline-block"
                >
                  Aramayı Temizle
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Filters & View Controls */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    // URL parametrelerini güncelle
                    const newParams = new URLSearchParams(searchParams);
                    if (category.id === 'all') {
                      newParams.delete('category');
                    } else {
                      newParams.set('category', category.id);
                    }
                    setSearchParams(newParams);
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                    selectedCategory === category.id
                      ? 'bg-slate-900 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-4">
              {/* Sort */}
              <div className="flex items-center gap-2">
                <FunnelIcon className="h-5 w-5 text-gray-600" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                >
                  {sortOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* View Mode */}
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-all ${
                    viewMode === 'grid' ? 'bg-white text-slate-900 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  <Squares2X2Icon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-all ${
                    viewMode === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  <ListBulletIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid - Big Cards */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600"></div>
          </div>
        ) : filteredProducts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {filteredProducts.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative rounded-2xl overflow-hidden shadow-xl group cursor-pointer bg-white"
              onClick={() => navigate(`/products/${product.id}`)}
            >
              <div className="relative h-80 overflow-hidden">
                <img 
                  src={product.image_url || 'https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=600&h=400&fit=crop&crop=center'} 
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  onError={(e) => {
                    console.log(`Image failed to load for ${product.name}:`, product.image_url);
                    e.target.src = 'https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=600&h=400&fit=crop&crop=center';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                
                {/* Rating & Reviews - Top Left */}
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-2 rounded-lg shadow-sm">
                  <div className="flex items-center space-x-1">
                    <StarIcon className="h-4 w-4 text-amber-400 fill-current" />
                    <span className="text-sm font-semibold text-gray-800">{product.rating?.toFixed(1) || '4.5'}</span>
                    <span className="text-sm text-gray-600">({product.reviews || '50'})</span>
                  </div>
                </div>

                {/* Sale Badge - Top Right */}
                {product.is_set && product.set_original_price > product.set_final_price ? (
                  <div className="absolute top-4 right-4 bg-blue-500 px-3 py-2 rounded-lg shadow-sm">
                    <span className="text-sm font-bold text-white">
                      🛋️ Takım %{Math.round((1 - product.set_final_price / product.set_original_price) * 100)} İndirim
                    </span>
                  </div>
                ) : product.sale_price ? (
                  <div className="absolute top-4 right-4 bg-red-500 px-3 py-2 rounded-lg shadow-sm">
                    <span className="text-sm font-bold text-white">
                      %{Math.round((1 - product.sale_price / product.price) * 100)} İndirim
                    </span>
                  </div>
                ) : (
                  <div className="absolute top-4 right-4 bg-slate-600 px-3 py-2 rounded-lg shadow-sm">
                    <span className="text-sm font-semibold text-white">
                      {product.category}
                    </span>
                  </div>
                )}
              </div>

              {/* Product Info - Separate Section */}
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {product.name}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {product.description}
                    </p>
                    <div className="flex items-center space-x-3">
                      {product.is_set ? (
                        <>
                          <span className="text-2xl font-bold text-blue-600">
                            ₺{(product.set_final_price || 0).toLocaleString()}
                          </span>
                          {product.set_original_price && product.set_original_price > product.set_final_price && (
                            <span className="text-lg text-gray-400 line-through">
                              ₺{product.set_original_price.toLocaleString()}
                            </span>
                          )}
                          <span className="text-sm bg-blue-100 text-blue-600 px-2 py-1 rounded">
                            🛋️ Takım
                          </span>
                        </>
                      ) : product.sale_price ? (
                        <>
                          {/* İndirimli ürün - sale_price varsa onu göster */}
                          <span className="text-2xl font-bold text-emerald-600">
                            ₺{(product.sale_price || 0).toLocaleString()}
                          </span>
                          <span className="text-lg text-gray-400 line-through">
                            ₺{(product.price || 0).toLocaleString()}
                          </span>
                        </>
                      ) : (
                        <>
                          {/* Normal ürün - price kar oranı uygulanmış satış fiyatı */}
                          <span className="text-2xl font-bold text-gray-900">
                            ₺{(product.price || 0).toLocaleString()}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex space-x-3 ml-6">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/products/${product.id}`);
                      }}
                      className="w-12 h-12 bg-slate-100 hover:bg-slate-700 hover:text-white text-slate-600 rounded-xl transition-all duration-300 flex items-center justify-center shadow-sm"
                      title="İncele"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        addToCart(product);
                      }}
                      className="w-12 h-12 bg-emerald-100 hover:bg-emerald-600 hover:text-white text-emerald-600 rounded-xl transition-all duration-300 flex items-center justify-center shadow-sm"
                      title="Sepete Ekle"
                    >
                      <ShoppingCartIcon className="h-5 w-5" />
                    </motion.button>
                  </div>
                </div>
              </div>

              {/* Hover effect overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">Henüz ürün bulunmuyor.</p>
          </div>
        )}

        {/* Load More */}
        <div className="text-center mt-12">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg"
          >
            Daha Fazla Ürün Yükle
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage; 